/* Sağ panel — seçili bloğun ayarları; seçim yoksa bütün sitenin teması.

   Metin burada yazılmıyor: metin önizlemede, yerinde düzenleniyor
   (canvas.ts). Burası metnin dışındaki her şey için: başlık düzeyi,
   bağlantı adresi, görsel, kart dizisinin ayrıntıları, renk, boşluk,
   görünürlük. */

import {
  FONTS,
  type Block,
  type BlockStyle,
  type Card,
  type FontName,
  type LinkButton,
  type NavLink,
  type Palette,
  type Reel,
  type Tokens
} from '@frawgraphy/shared';
import { el, replace } from '../dom.ts';
import { paletteDefaults } from '../design/defaults.ts';
import { blankBlock, blankItem, TYPE_LABELS } from './blank.ts';
import { itemOp, itemsOf, locate, tableOp, takenIds } from './doc.ts';
import { colorField, contrastNote, group, mediaField, mediaUrl, rangeField, row, selectField, textField, toggleField } from './fields.ts';

export type InspectorCtx = {
  home(): Block[];
  mutate(fn: (home: Block[]) => string | null | void, after?: { select?: string | null }): void;
  select(id: string | null): void;
  toast(message: string, kind?: 'ok' | 'bad'): void;
  fail(err: unknown): void;
  theme(): 'dark' | 'light';
  tokens(): Tokens;
  setTokens(fn: (t: Tokens) => void): void;
};

type Of<T extends Block['type']> = Extract<Block, { type: T }>;

export function renderInspector(host: HTMLElement, id: string | null, ctx: InspectorCtx): void {
  const loc = id ? locate(ctx.home(), id) : null;
  if (!loc) {
    replace(host, ...themeInspector(ctx));
    return;
  }
  const block = loc.block;
  /** Bu bloğu değiştiren bir işlem — yeniden bulup uygular, seçili tutar. */
  const edit = (fn: (b: Block) => void) =>
    ctx.mutate((home) => {
      const b = locate(home, block.id)?.block;
      if (b) fn(b);
      return block.id;
    });
  const toast = (m: string) => ctx.toast(m, 'bad');

  const parts: (HTMLElement | null)[] = [
    el(
      'div',
      { class: 'design-head' },
      el('p', { class: 'tile-name', text: TYPE_LABELS[block.type] }),
      el('p', { class: 'dim', text: `#${block.id}` }),
      loc.parent ? el('button', { type: 'button', text: `↑ ${TYPE_LABELS[loc.parent.type]}`, title: 'Kabı seç', onclick: () => ctx.select(loc.parent!.id) }) : null
    ),
    ...typeSettings(block, edit, ctx, toast),
    styleGroup(block, edit, ctx, toast)
  ];
  replace(host, ...parts.filter((p): p is HTMLElement => p !== null));
}

/* ── Tipe özel ayarlar ──────────────────────────────────── */

function typeSettings(block: Block, edit: (fn: (b: Block) => void) => void, ctx: InspectorCtx, toast: (m: string) => void): HTMLElement[] {
  const fail = ctx.fail;
  switch (block.type) {
    case 'heading':
      return [
        group(
          'Başlık',
          selectField('Düzey', [['1', 'Başlık 1 (sayfa)'], ['2', 'Başlık 2 (bölüm)'], ['3', 'Başlık 3'], ['4', 'Başlık 4']], String(block.level), (v) =>
            edit((b) => ((b as Of<'heading'>).level = Number(v) as 1 | 2 | 3 | 4))
          )
        )
      ];
    case 'paragraph':
      return [
        group(
          'Paragraf',
          selectField('Tür', [['body', 'Normal'], ['lede', 'Bölüm girişi'], ['lead', 'Öne çıkan'], ['small', 'Küçük']], block.variant ?? 'body', (v) =>
            edit((b) => {
              const p = b as Of<'paragraph'>;
              if (v === 'body') delete p.variant;
              else p.variant = v as 'lede' | 'lead' | 'small';
            })
          )
        )
      ];
    case 'quote':
      return [
        group(
          'Alıntı',
          toggleField('Kaynak satırı', Boolean(block.cite), (on) =>
            edit((b) => {
              const q = b as Of<'quote'>;
              if (on) q.cite = { tr: 'Kaynak' };
              else delete q.cite;
            })
          )
        )
      ];
    case 'list':
      return [group('Liste', toggleField('Numaralı', block.ordered, (on) => edit((b) => ((b as Of<'list'>).ordered = on))))];
    case 'table': {
      const t = block;
      const rows = t.rows.length;
      const cols = t.rows[0]?.length ?? 1;
      return [
        group(
          'Tablo',
          toggleField('İlk satır başlık', t.header, (on) => edit((b) => ((b as Of<'table'>).header = on))),
          el('p', { class: 'dim', text: `${rows} satır × ${cols} sütun` }),
          el(
            'div',
            { class: 'row' },
            el('button', { type: 'button', text: '+ Satır', onclick: () => edit((b) => tableOp(b as Of<'table'>, 'addRow', rows - 1)) }),
            el('button', { type: 'button', text: '+ Sütun', onclick: () => edit((b) => tableOp(b as Of<'table'>, 'addCol', cols - 1)) }),
            el('button', { type: 'button', text: '− Satır', disabled: rows < 2, onclick: () => edit((b) => tableOp(b as Of<'table'>, 'removeRow', rows - 1)) }),
            el('button', { type: 'button', text: '− Sütun', disabled: cols < 2, onclick: () => edit((b) => tableOp(b as Of<'table'>, 'removeCol', cols - 1)) })
          ),
          el('p', { class: 'dim', text: 'Hücrelere önizlemede tıklayıp yazın; Tab sonraki hücreye geçer.' })
        )
      ];
    }
    case 'buttons':
      return [
        group(
          'Düğmeler',
          ...block.items.map((btn, i) => buttonEditor(btn, `Düğme ${i + 1}`, (fn) => edit((b) => fn((b as Of<'buttons'>).items[i]!)), toast)),
          addItemButton(block, ctx, 'Düğme ekle')
        )
      ];
    case 'image':
      return [
        group(
          'Görsel',
          mediaField('Kare', block.media, (m) => m && edit((b) => ((b as Of<'image'>).media = m)), fail, false),
          selectField('Oran', [['auto', 'Kendi oranı'], ['1:1', 'Kare'], ['4:5', '4:5 dikey'], ['3:2', '3:2 yatay'], ['16:9', '16:9 geniş']], block.ratio ?? 'auto', (v) =>
            edit((b) => ((b as Of<'image'>).ratio = v as Of<'image'>['ratio']))
          ),
          toggleField('Kenardan kenara', Boolean(block.bleed), (on) => edit((b) => ((b as Of<'image'>).bleed = on || undefined))),
          toggleField('Altyazı', Boolean(block.caption), (on) =>
            edit((b) => {
              const img = b as Of<'image'>;
              if (on) img.caption = { tr: 'Altyazı' };
              else delete img.caption;
            })
          )
        )
      ];
    case 'gallery':
      return [
        group(
          'Galeri',
          selectField('Sütun', [['2', '2'], ['3', '3'], ['4', '4']], String(block.cols), (v) => edit((b) => ((b as Of<'gallery'>).cols = Number(v) as 2 | 3 | 4))),
          ...block.items.map((m, i) =>
            mediaField(`Kare ${i + 1}`, m, (next) => next && edit((b) => ((b as Of<'gallery'>).items[i] = { ...next, id: m.id })), fail, false)
          ),
          addItemButton(block, ctx, 'Kare ekle')
        )
      ];
    case 'columns':
      return [
        group(
          'Sütunlar',
          selectField('Sütun sayısı', [['2', '2'], ['3', '3'], ['4', '4']], String(block.columns.length), (v) =>
            ctx.mutate((home) => {
              const c = locate(home, block.id)?.block as Of<'columns'> | undefined;
              if (!c) return null;
              const want = Number(v);
              const taken = takenIds(home);
              while (c.columns.length < want) c.columns.push(blankItem(c, taken) as Of<'columns'>['columns'][number]);
              if (c.columns.length > want) {
                const extra = c.columns.splice(want).flatMap((col) => col.blocks);
                c.columns[want - 1]!.blocks.push(...extra);
              }
              return block.id;
            })
          ),
          el('p', { class: 'dim', text: 'Azaltınca kaldırılan sütunların blokları son sütuna taşınır.' })
        )
      ];
    case 'cards':
      return [
        group(
          'Kartlar',
          selectField('Görünüm', [['plain', 'Sade kart'], ['ai', 'Yapay zekâ kartı (büyütmeli)'], ['service', 'Hizmet satırı']], block.variant, (v) =>
            edit((b) => ((b as Of<'cards'>).variant = v as Of<'cards'>['variant']))
          ),
          ...block.items.map((card, i) => cardEditor(block, card, i, edit, ctx)),
          addItemButton(block, ctx, 'Kart ekle')
        )
      ];
    case 'reels':
      return [group('Video kartları', ...block.items.map((r, i) => reelEditor(r, i, edit, toast)), addItemButton(block, ctx, 'Film ekle'))];
    case 'facts':
      return [
        group(
          'Bilgi listesi',
          selectField('Görünüm', [['plain', 'Etiket — değer'], ['stats', 'Büyük sayılar']], block.variant === 'contact' ? 'plain' : block.variant, (v) =>
            edit((b) => ((b as Of<'facts'>).variant = v as Of<'facts'>['variant']))
          ),
          el('p', { class: 'dim', text: 'Etiketleri ve değerleri önizlemede yazın.' }),
          addItemButton(block, ctx, 'Satır ekle')
        )
      ];
    case 'split':
      return [
        group(
          'Görsel + metin',
          mediaField('Görsel', block.media, (m) => m && edit((b) => ((b as Of<'split'>).media = m)), fail, false),
          toggleField('Görsel altyazısı', Boolean(block.caption), (on) =>
            edit((b) => {
              const s = b as Of<'split'>;
              if (on) s.caption = { tr: 'Altyazı' };
              else delete s.caption;
            })
          ),
          addChildButton(block.id, ctx, 'Metin sütununa paragraf ekle')
        )
      ];
    case 'cta':
      return [group('Çağrı', buttonEditor(block.button, 'Düğme', (fn) => edit((b) => fn((b as Of<'cta'>).button)), toast))];
    case 'coverflow':
      return [coverflowEditor(block, edit, ctx)];
    case 'contact':
      return [
        group(
          'İletişim',
          el('p', { class: 'dim', text: 'Formun alanları sabit; sağdaki bilgi satırlarını ve notu önizlemede yazın.' }),
          addItemButton(block, ctx, 'Bilgi satırı ekle'),
          toggleField('Alt not', Boolean(block.note), (on) =>
            edit((b) => {
              const c = b as Of<'contact'>;
              if (on) c.note = { tr: 'Not' };
              else delete c.note;
            })
          )
        )
      ];
    case 'hero':
      return [heroEditor(block, edit, ctx, toast)];
    case 'section':
      return [
        group(
          'Bölüm',
          textField('Bölüm kimliği (adreste #…)', block.anchor, 'iletisim', (v) => {
            if (!/^[a-z][a-z0-9-]{1,30}$/.test(v)) return toast('Kimlik küçük harf, rakam ve tire; harfle başlar.');
            edit((b) => ((b as Of<'section'>).anchor = v));
          }),
          toggleField('Koyu zemin (sıra ile değişen)', block.alt, (on) => edit((b) => ((b as Of<'section'>).alt = on))),
          el(
            'div',
            { class: 'row' },
            el('button', { type: 'button', text: '+ Üst etiket', onclick: () => addHead(block.id, 'eyebrow', ctx) }),
            el('button', { type: 'button', text: '+ Başlık', onclick: () => addHead(block.id, 'heading', ctx) }),
            el('button', { type: 'button', text: '+ Giriş', onclick: () => addHead(block.id, 'paragraph', ctx) })
          ),
          addChildButton(block.id, ctx, 'Bölümün sonuna paragraf ekle')
        )
      ];
    case 'html':
      return [group('Eski içerik', el('p', { class: 'dim', text: 'Taşıma sırasında tanınmayan bir parça. Düzenlenemez; silinebilir ya da taşınabilir.' }))];
    case 'nav':
      return [
        group(
          'Menü',
          el('p', { class: 'dim', text: 'Üst menü ve telefon menüsü aynı liste. Bağlantı yazılarını önizlemede yazın; ana sayfada gizlenen bir bölümün bağlantısı kendiliğinden düşer.' }),
          ...block.items.map((link, i) => linkEditor(link, i, block.items.length, 'items', block.id, edit, ctx, toast)),
          addItemButton(block, ctx, 'Bağlantı ekle')
        )
      ];
    case 'footer':
      return [
        group(
          'Footer',
          el('p', { class: 'dim', text: 'Marka, bağlantı yazıları ve telif satırı önizlemede düzenlenir.' }),
          ...block.links.map((link, i) => linkEditor(link, i, block.links.length, 'links', block.id, edit, ctx, toast)),
          addItemButton(block, ctx, 'Bağlantı ekle')
        )
      ];
    case 'page':
      return [
        group(
          'Sayfa başlığı',
          el('p', { class: 'dim', text: 'Sayfanın üstündeki başlık bölgesi. Altına istediğiniz blokları ekleyebilirsiniz.' }),
          el(
            'div',
            { class: 'row' },
            el('button', { type: 'button', text: '+ Üst etiket', onclick: () => addHead(block.id, 'eyebrow', ctx) }),
            el('button', { type: 'button', text: '+ Giriş', onclick: () => addHead(block.id, 'paragraph', ctx) })
          ),
          addChildButton(block.id, ctx, 'Başlığın altına paragraf ekle')
        )
      ];
    case 'pagecta':
      return [
        group(
          'Yönlendirme kutusu',
          ...block.buttons.map((btn, i) => buttonEditor(btn, `Düğme ${i + 1}`, (fn) => edit((b) => fn((b as Of<'pagecta'>).buttons[i]!)), toast)),
          addItemButton(block, ctx, 'Düğme ekle')
        )
      ];
    default:
      return [];
  }
}

function buttonEditor(btn: LinkButton, title: string, edit: (fn: (b: LinkButton) => void) => void, toast: (m: string) => void): HTMLElement {
  return el(
    'div',
    { class: 'design-sub' },
    el('p', { class: 'dim', text: `${title} — yazısını önizlemede düzenleyin` }),
    textField('Bağlantı', btn.href, '#contact, works.html, https://…', (v) => {
      if (!v) return toast('Bağlantı boş olamaz.');
      edit((b) => (b.href = v));
    }),
    selectField('Görünüm', [['primary', 'Dolu'], ['ghost', 'Çizgili'], ['plain', 'Sade']], btn.variant, (v) => edit((b) => (b.variant = v as LinkButton['variant']))),
    toggleField('Ok simgesi', Boolean(btn.arrow), (on) => edit((b) => (b.arrow = on || undefined)))
  );
}

/** Menü ya da footer bağlantısı: adres, İngilizce işareti, sıra, sil. */
function linkEditor(
  link: NavLink,
  i: number,
  count: number,
  field: 'items' | 'links',
  blockId: string,
  edit: (fn: (b: Block) => void) => void,
  ctx: InspectorCtx,
  toast: (m: string) => void
): HTMLElement {
  const list = (b: Block) => (b as unknown as Record<'items' | 'links', NavLink[]>)[field];
  return el(
    'details',
    { class: 'design-sub' },
    el('summary', { text: `${i + 1}. ${link.label.tr.replace(/&amp;/g, '&')}` }),
    textField('Adres', link.href, '#contact, blog.html, https://instagram.com/…', (v) => {
      if (!v) return toast('Adres boş olamaz.');
      edit((b) => (list(b)[i]!.href = v));
    }),
    toggleField('Yazı İngilizce bir sözcük', link.lang === 'en', (on) => edit((b) => (on ? (list(b)[i]!.lang = 'en') : delete list(b)[i]!.lang))),
    el(
      'div',
      { class: 'row' },
      el('button', { type: 'button', text: '↑', disabled: i === 0, onclick: () => ctx.mutate((home) => itemOp(home, blockId, link.id, 'up') && blockId) }),
      el('button', { type: 'button', text: '↓', disabled: i === count - 1, onclick: () => ctx.mutate((home) => itemOp(home, blockId, link.id, 'down') && blockId) }),
      el('button', { type: 'button', text: 'Sil', class: 'danger', onclick: () => ctx.mutate((home) => itemOp(home, blockId, link.id, 'remove') && blockId) })
    )
  );
}

function cardEditor(block: Of<'cards'>, card: Card, i: number, edit: (fn: (b: Block) => void) => void, ctx: InspectorCtx): HTMLElement {
  const set = (fn: (c: Card) => void) => edit((b) => fn((b as Of<'cards'>).items[i]!));
  const title = card.title.tr.replace(/<[^>]+>/g, '') || `Kart ${i + 1}`;
  const details = el('details', { class: 'design-sub' }, el('summary', { text: `${i + 1}. ${title}` }));
  if (block.variant !== 'service') {
    details.append(mediaField('Görsel', card.media, (m) => set((c) => (m ? (c.media = m) : delete c.media)), ctx.fail));
    details.append(textField('Yıl', card.year ?? '', '2026', (v) => set((c) => (v ? (c.year = v) : delete c.year))));
  }
  if (block.variant === 'plain') {
    details.append(textField('Bağlantı (isteğe bağlı)', card.href ?? '', 'album-deri-inci.html', (v) => set((c) => (v ? (c.href = v) : delete c.href))));
  }
  if (block.variant === 'service') {
    details.append(toggleField('Başlık İngilizce bir sözcük', card.titleLang === 'en', (on) => set((c) => (on ? (c.titleLang = 'en') : delete c.titleLang))));
  }
  details.append(
    el(
      'div',
      { class: 'row' },
      el('button', { type: 'button', text: '↑', title: 'Öne', onclick: () => ctx.mutate((home) => itemOp(home, block.id, card.id, 'up') && block.id) }),
      el('button', { type: 'button', text: '↓', title: 'Arkaya', onclick: () => ctx.mutate((home) => itemOp(home, block.id, card.id, 'down') && block.id) }),
      el('button', { type: 'button', text: 'Çoğalt', onclick: () => ctx.mutate((home) => itemOp(home, block.id, card.id, 'duplicate') && block.id) }),
      el('button', { type: 'button', text: 'Sil', class: 'danger', onclick: () => ctx.mutate((home) => itemOp(home, block.id, card.id, 'remove') && block.id) })
    )
  );
  return details;
}

function reelEditor(r: Reel, i: number, edit: (fn: (b: Block) => void) => void, toast: (m: string) => void): HTMLElement {
  const set = (fn: (x: Reel) => void) => edit((b) => fn((b as Of<'reels'>).items[i]!));
  const [fx, fy] = (r.focus ?? '50% 50%').split(' ').map((v) => Number.parseInt(v, 10));
  return el(
    'details',
    { class: 'design-sub' },
    el('summary', { text: `${i + 1}. ${r.title.tr}` }),
    textField('Kapak görseli', r.poster ?? '', 'https://… ya da assets/img/…', (v) => set((x) => (v ? (x.poster = v) : delete x.poster))),
    textField('Video dosyası', r.video ?? '', 'assets/video/film.mp4', (v) => set((x) => (v ? (x.video = v) : delete x.video))),
    textField('Süre', r.time, '01:24', (v) => (/^\d{1,2}:\d{2}$/.test(v) ? set((x) => (x.time = v)) : toast('Süre 01:24 biçiminde olmalı.'))),
    rangeField('Odak yatay', (fx ?? 50) / 100, 0, 1, (v) => set((x) => (x.focus = `${Math.round(v * 100)}% ${fy ?? 50}%`)), 0.01),
    rangeField('Odak dikey', (fy ?? 50) / 100, 0, 1, (v) => set((x) => (x.focus = `${fx ?? 50}% ${Math.round(v * 100)}%`)), 0.01)
  );
}

function coverflowEditor(block: Of<'coverflow'>, edit: (fn: (b: Block) => void) => void, ctx: InspectorCtx): HTMLElement {
  const list = el(
    'div',
    { class: 'design-thumbs' },
    ...block.photos.map((id, i) =>
      el(
        'figure',
        {},
        el('img', { src: mediaUrl({ photo: id }) ?? '', alt: id }),
        el(
          'div',
          { class: 'row' },
          el('button', { type: 'button', text: '←', disabled: i === 0, onclick: () => edit((b) => swap((b as Of<'coverflow'>).photos, i, i - 1)) }),
          el('button', { type: 'button', text: '→', disabled: i === block.photos.length - 1, onclick: () => edit((b) => swap((b as Of<'coverflow'>).photos, i, i + 1)) }),
          el('button', { type: 'button', text: '×', onclick: () => edit((b) => (b as Of<'coverflow'>).photos.splice(i, 1)) })
        )
      )
    )
  );
  return group(
    'Seçili işler',
    el('p', { class: 'dim', text: 'Kareler arşivdeki kimlikleriyle; sırası karuselin sırası.' }),
    list,
    mediaField('Kare ekle', undefined, (m) => m?.photo && edit((b) => (b as Of<'coverflow'>).photos.push(m.photo!)), ctx.fail)
  );
}

function heroEditor(block: Of<'hero'>, edit: (fn: (b: Block) => void) => void, ctx: InspectorCtx, toast: (m: string) => void): HTMLElement {
  return group(
    'Giriş',
    mediaField('Arka plan karesi (boşsa bugünkü)', block.media, (m) => edit((b) => (m ? ((b as Of<'hero'>).media = m) : delete (b as Of<'hero'>).media)), ctx.fail),
    el('p', { class: 'design-label', text: 'Büyük başlığın satırları' }),
    ...block.lines.map((line, i) =>
      el(
        'div',
        { class: 'design-sub' },
        textField(`Satır ${i + 1}`, line.text.replace(/&amp;/g, '&'), 'Fashion', (v) => (v ? edit((b) => ((b as Of<'hero'>).lines[i]!.text = v)) : toast('Satır boş olamaz — silin.'))),
        toggleField('İçeri girintili', Boolean(line.indent), (on) => edit((b) => ((b as Of<'hero'>).lines[i]!.indent = on || undefined))),
        toggleField('Sonunda "&"', Boolean(line.amp), (on) => edit((b) => ((b as Of<'hero'>).lines[i]!.amp = on || undefined))),
        el('button', { type: 'button', text: 'Satırı sil', class: 'danger', disabled: block.lines.length < 2, onclick: () => edit((b) => (b as Of<'hero'>).lines.splice(i, 1)) })
      )
    ),
    el('button', {
      type: 'button',
      text: '+ Satır',
      disabled: block.lines.length >= 5,
      onclick: () =>
        ctx.mutate((home) => {
          const h = locate(home, block.id)?.block as Of<'hero'> | undefined;
          if (!h) return null;
          const taken = takenIds(home);
          let n = 1;
          while (taken.has(`line-${n}`)) n += 1;
          h.lines.push({ id: `line-${n}`, text: 'Yeni' });
          return block.id;
        })
    }),
    ...block.buttons.map((btn, i) => buttonEditor(btn, `Düğme ${i + 1}`, (fn) => edit((b) => fn((b as Of<'hero'>).buttons[i]!)), toast))
  );
}

function swap<T>(list: T[], a: number, b: number): void {
  const [x] = list.splice(a, 1);
  list.splice(b, 0, x!);
}

function addItemButton(block: Block, ctx: InspectorCtx, label: string): HTMLElement {
  return el('button', {
    type: 'button',
    class: 'design-add-btn',
    text: `+ ${label}`,
    onclick: () =>
      ctx.mutate((home) => {
        const b = locate(home, block.id)?.block;
        if (!b) return null;
        const item = blankItem(b, takenIds(home));
        if (!item) return null;
        const list = itemsOf(b) as unknown[] | null;
        if (!list) return null;
        list.push(item);
        return block.id;
      })
  });
}

function addChildButton(blockId: string, ctx: InspectorCtx, label: string): HTMLElement {
  return el('button', {
    type: 'button',
    class: 'design-add-btn',
    text: `+ ${label}`,
    onclick: () => {
      let created = '';
      ctx.mutate((home) => {
        const b = locate(home, blockId)?.block;
        if (!b || (b.type !== 'section' && b.type !== 'split' && b.type !== 'page')) return null;
        const p = blankBlock('paragraph', takenIds(home));
        created = p.id;
        b.blocks.push(p);
        return p.id;
      });
      if (created) ctx.select(created);
    }
  });
}

function addHead(sectionId: string, type: 'eyebrow' | 'heading' | 'paragraph', ctx: InspectorCtx): void {
  ctx.mutate((home) => {
    const s = locate(home, sectionId)?.block;
    if (s?.type !== 'section' && s?.type !== 'page') return null;
    const b = blankBlock(type, takenIds(home));
    if (b.type === 'paragraph') b.variant = 'lede';
    const at = type === 'eyebrow' ? 0 : type === 'heading' ? s.head.findIndex((x) => x.type !== 'eyebrow') : s.head.length;
    s.head.splice(at < 0 ? s.head.length : at, 0, b);
    return b.id;
  });
}

/* ── Görünüm (her blokta) ───────────────────────────────── */

function styleGroup(block: Block, edit: (fn: (b: Block) => void) => void, ctx: InspectorCtx, toast: (m: string) => void): HTMLElement {
  const s = block.style ?? {};
  const set = <K extends keyof BlockStyle>(key: K, value: BlockStyle[K] | undefined) =>
    edit((b) => {
      const next: BlockStyle = { ...(b.style ?? {}) };
      if (value === undefined || value === '' || value === false) delete next[key];
      else next[key] = value;
      if (Object.keys(next).length) b.style = next;
      else delete b.style;
    });
  const theme = paletteDefaults()[ctx.theme()];
  const over = ctx.tokens()[ctx.theme()];
  const fonts = (Object.keys(FONTS) as FontName[]).map((f) => [f, f] as [string, string]);
  const space: [string, string][] = [['', 'Varsayılan'], ['none', 'Yok'], ['s', 'Az'], ['m', 'Orta'], ['l', 'Çok'], ['xl', 'En çok']];
  return group(
    'Görünüm',
    selectField('Hiza', [['', 'Varsayılan'], ['left', 'Sol'], ['center', 'Orta'], ['right', 'Sağ'], ['justify', 'İki yana']], s.align ?? '', (v) => set('align', (v || undefined) as BlockStyle['align'])),
    selectField('Genişlik', [['', 'Varsayılan'], ['narrow', 'Dar'], ['text', 'Metin'], ['wide', 'Geniş'], ['full', 'Tam']], s.width ?? '', (v) => set('width', (v || undefined) as BlockStyle['width'])),
    colorField('Yazı rengi', s.color, over.fg ?? theme.fg, (v) => set('color', v), toast),
    colorField('Zemin', s.bg, over.bg ?? theme.bg, (v) => set('bg', v), toast),
    s.color || s.bg ? contrastNote(s.bg ?? over.bg ?? theme.bg, s.color ?? over.fg ?? theme.fg) : null,
    rangeField('Yazı boyutu', s.scale ?? 1, 0.5, 3, (v) => set('scale', v === 1 ? undefined : v)),
    selectField('Yazı tipi', [['', 'Varsayılan'], ...fonts], s.font ?? '', (v) => set('font', (v || undefined) as FontName | undefined)),
    selectField('Kalınlık', [['', 'Varsayılan'], ['300', 'İnce'], ['400', 'Normal'], ['500', 'Orta'], ['600', 'Yarı kalın'], ['700', 'Kalın']], String(s.weight ?? ''), (v) =>
      set('weight', (v ? Number(v) : undefined) as BlockStyle['weight'])
    ),
    selectField('İç boşluk (dikey)', space, s.padY ?? '', (v) => set('padY', (v || undefined) as BlockStyle['padY'])),
    selectField('Dış boşluk (dikey)', space, s.marginY ?? '', (v) => set('marginY', (v || undefined) as BlockStyle['marginY'])),
    selectField('Göster', [['', 'Her cihazda'], ['desktop', 'Yalnızca masaüstü'], ['mobile', 'Yalnızca telefon']], s.only ?? '', (v) => set('only', (v || undefined) as BlockStyle['only'])),
    toggleField('Kaydırınca belirsin', Boolean(block.reveal), (on) => edit((b) => (on ? (b.reveal = true) : delete b.reveal))),
    toggleField('Gizle (sayfada basılmaz)', Boolean(s.hidden), (on) => set('hidden', on || undefined)),
    block.style ? el('button', { type: 'button', text: 'Görünümü sıfırla', class: 'danger', onclick: () => edit((b) => delete b.style) }) : null
  );
}

/* ── Genel tema ─────────────────────────────────────────── */

function themeInspector(ctx: InspectorCtx): HTMLElement[] {
  const defaults = paletteDefaults();
  const tokens = ctx.tokens();
  const toast = (m: string) => ctx.toast(m, 'bad');
  const out: HTMLElement[] = [
    el('p', { class: 'tile-name', text: 'Genel tema' }),
    el('p', { class: 'dim', text: 'Bütün sayfalara uygulanır. Bir bloğu ayarlamak için önizlemede tıklayın.' })
  ];
  for (const mode of ['dark', 'light'] as const) {
    const p = tokens[mode];
    const d = defaults[mode];
    const rows: [keyof Palette, string][] = [
      ['bg', 'Zemin'],
      ['bgElev', 'İkinci zemin'],
      ['fg', 'Yazı'],
      ['fgMuted', 'Silik yazı'],
      ['accent', 'Vurgu']
    ];
    out.push(
      group(
        mode === 'dark' ? 'Koyu tema renkleri' : 'Açık tema renkleri',
        ...rows.map(([key, label]) =>
          colorField(label, p[key], d[key], (v) =>
            ctx.setTokens((t) => {
              if (v) t[mode][key] = v;
              else delete t[mode][key];
            }), toast)
        ),
        contrastNote(p.bg ?? d.bg, p.fg ?? d.fg)
      )
    );
  }
  const fontOpts = (role: 'display' | 'body'): [string, string][] => [
    ['', 'Varsayılan'],
    ...(Object.keys(FONTS) as FontName[]).filter((f) => role === 'display' || FONTS[f].role === 'body').map((f) => [f, f] as [string, string])
  ];
  out.push(
    group(
      'Yazı tipleri',
      selectField('Başlıklar', fontOpts('display'), tokens.fontDisplay ?? '', (v) =>
        ctx.setTokens((t) => (v ? (t.fontDisplay = v as FontName) : delete t.fontDisplay))
      ),
      selectField('Metin', fontOpts('body'), tokens.fontBody ?? '', (v) => ctx.setTokens((t) => (v ? (t.fontBody = v as FontName) : delete t.fontBody))),
      rangeField('Bütün yazıların boyutu', tokens.typeScale ?? 1, 0.8, 1.3, (v) => ctx.setTokens((t) => (v === 1 ? delete t.typeScale : (t.typeScale = v))))
    ),
    row('İpucu', el('span', { class: 'dim', text: 'Metne tıklayıp yazın; Enter yeni paragraf, boş paragrafta / blok menüsü.' }))
  );
  return out;
}
